/* eslint-disable no-prototype-builtins */

export const isStructureWithoutData = (item) => item.type === 'structure' && item.id !== 'wrapper' && !item.structureData;

export const isAdvancedWithoutValue = (item) => item.type === 'advanced' && item.id !== 'wrapper' && !item.value;

export const isExist = (item) => item.canExist;

const NLQ_FIELD_ID = 'CIT.NLQL';
const NLQ_ALLOWED_FIELD_IDS = new Set([NLQ_FIELD_ID, 'CBI.BICIT', 'STRUCTURE.QL']);
const PREVIEW_SEMANTIC_FLAG = 'isPreviewSemanticHistoryFlow';

// Helper function to check if children have no values
const hasNoValues = (children) => {
  if (!children || children.length === 0) return true;
  return children.every((child) => !child.hasOwnProperty('value') || !child.value);
};

// Helper function to check if an item should be considered valid
const isValidItem = (item) => isStructureWithoutData(item) || isAdvancedWithoutValue(item);

// Helper function to check wrapper items recursively
const checkWrapperItems = (items) => {
  if (!items || items.length === 0) return true;

  return items.every((item) => {
    if (isValidItem(item)) return true;

    if (item.exist) return false;

    if (item.id === 'wrapper' && item.items?.length > 0) {
      return checkWrapperItems(item.items);
    }

    if (item.children?.length > 0) {
      return hasNoValues(item.children);
    }

    return false;
  });
};

const collectFieldIds = (candidate, acc = []) => {
  if (!candidate) {
    return acc;
  }

  if (Array.isArray(candidate)) {
    candidate.forEach((item) => collectFieldIds(item, acc));
    return acc;
  }

  if (candidate?.type === 'structure' && candidate?.structureData && typeof candidate?.id === 'string') {
    acc.push(candidate.id);
  }

  if (typeof candidate?.id === 'string' && candidate?.id?.includes('.')) {
    acc.push(candidate.id);
  }

  if (Array.isArray(candidate?.fields)) {
    candidate?.fields?.forEach((field) => {
      if (typeof field?.id === 'string' && field?.id?.includes('.')) {
        acc.push(field.id);
      }
    });
  }

  if (Array.isArray(candidate?.children)) {
    candidate?.children?.forEach((child) => {
      if (typeof child?.id === 'string' && child?.id?.includes('.')) {
        acc.push(child.id);
      }
    });
  }

  if (Array.isArray(candidate?.items)) {
    collectFieldIds(candidate.items, acc);
  }

  if (Array.isArray(candidate?.facts)) {
    collectFieldIds(candidate.facts, acc);
  }

  return acc;
};

const hasStructureQuerylet = (candidate) => {
  if (!candidate) {
    return false;
  }

  if (Array.isArray(candidate)) {
    return candidate.some((item) => hasStructureQuerylet(item));
  }

  if (candidate.type === 'structure') {
    return true;
  }

  return (
    hasStructureQuerylet(candidate.children) ||
    hasStructureQuerylet(candidate.items) ||
    hasStructureQuerylet(candidate.facts) ||
    hasStructureQuerylet(candidate.structures)
  );
};

const isPreviewSemanticHistoryFlow = (nodesList) => {
  const hasSemanticFlag = (candidate) => {
    if (!candidate) {
      return false;
    }

    if (Array.isArray(candidate)) {
      return candidate.some((item) => hasSemanticFlag(item));
    }

    const hasMarker = candidate?.[PREVIEW_SEMANTIC_FLAG] || candidate?.isSemanticSearchIncluded;
    if (hasMarker) {
      return true;
    }

    return (
      hasSemanticFlag(candidate?.children) ||
      hasSemanticFlag(candidate?.items) ||
      hasSemanticFlag(candidate?.fields) ||
      hasSemanticFlag(candidate?.facts) ||
      hasSemanticFlag(candidate?.structures)
    );
  };

  return hasStructureQuerylet(nodesList) && hasSemanticFlag(nodesList);
};

export const checkChildrenValues = (nodesList) => {
  if (!nodesList || nodesList.length === 0) return true;

  return nodesList.every((item) => {
    if (isValidItem(item)) return true;

    if (item.exist) return false;

    if (item.children?.length > 0) {
      return hasNoValues(item.children);
    }

    if (item.id === 'wrapper' && item.items?.length > 0) {
      return checkWrapperItems(item.items);
    }

    return false;
  });
};

export function getUnsupportedNlqCombinationReason(nodesList) {
  const ids = [...new Set(collectFieldIds(nodesList))];
  if (!ids.includes(NLQ_FIELD_ID)) {
    return null;
  }

  const hasStructure = hasStructureQuerylet(nodesList);
  const isPreviewFlow = isPreviewSemanticHistoryFlow(nodesList);

  // For pure Query Builder flow, NLQL + structure is prohibited.
  if (hasStructure && !isPreviewFlow) {
    return 'structure';
  }

  // Preview -> Edit flow allows NLQL + TAK + structure only.
  const hasNonAllowedField = ids.some((id) => !NLQ_ALLOWED_FIELD_IDS.has(id));
  if (hasNonAllowedField) {
    return 'non-tak';
  }

  return null;
}

export const hasUnsupportedNlqCombination = (nodesList) => getUnsupportedNlqCombinationReason(nodesList) !== null;
